import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/apiClient';
import Layout from '../components/layout/Layout';

const Profile = () => {
  const { currentUser, updateUserData } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Load user data when component mounts
  useEffect(() => {
    if (currentUser) {
      setFormData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        organization: currentUser.organization || ''
      });
    }
  }, [currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Only send name and organization for update (email cannot be changed)
      const updateData = {
        name: formData.name,
        organization: formData.organization
      };

      const response = await apiClient.patch('/auth/update-me', updateData);
      
      // Update user data in context
      if (response && response.data && response.data.user) {
        updateUserData(response.data.user);
        setSuccess('Profile updated successfully');
        setIsEditing(false);
      } else {
        console.error('Unexpected response format:', response);
        setError('Failed to update profile: Invalid response format');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset form to current user data
    if (currentUser) {
      setFormData({
        name: currentUser.name || '',
        email: currentUser.email || '',
        organization: currentUser.organization || ''
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  // Helper function to format subscription type for display
  const formatSubscriptionType = (type) => {
    if (!type) return 'Free';
    
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  // Helper function to calculate days remaining in subscription
  const getDaysRemaining = (expiryDate) => {
    if (!expiryDate) return 'N/A';
    
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? `${diffDays} days` : 'Expired';
  };

  return (
    <Layout>
      <div className="container mx-auto py-8 px-4 max-w-3xl">
        <div className="bg-white rounded-lg shadow-md overflow-hidden p-4">
          <div className="p-6 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">Your Profile</h1>
            <p className="text-sm text-gray-600 mt-1">
              View and manage your account information
            </p>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 p-4 rounded-md bg-red-50 border border-red-200">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {success && (
              <div className="mb-4 p-4 rounded-md bg-green-50 border border-green-200">
                <p className="text-sm text-green-600">{success}</p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Full Name
                  </label>
                  <div className="mt-1">
                    <input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`form-input block w-full rounded-md ${!isEditing ? 'bg-gray-100' : ''}`}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      disabled={true}
                      className="form-input block w-full rounded-md bg-gray-100"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                      <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Cannot change</span>
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Email address cannot be changed. This is your unique identifier.
                  </p>
                </div>

                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-gray-700">
                    Organization
                  </label>
                  <div className="mt-1">
                    <input
                      id="organization"
                      name="organization"
                      type="text"
                      value={formData.organization}
                      onChange={handleChange}
                      disabled={!isEditing}
                      className={`form-input block w-full rounded-md ${!isEditing ? 'bg-gray-100' : ''}`}
                    />
                  </div>
                </div>

                {/* Account Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Account Information</h3>
                  <dl className="mt-4 grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Account Type</dt>
                      <dd className="mt-1 text-sm text-gray-900">{currentUser?.role || 'User'}</dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Member Since</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {currentUser?.createdAt 
                          ? new Date(currentUser.createdAt).toLocaleDateString() 
                          : 'Unknown'}
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Email Verified</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {currentUser?.emailVerified ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Not Verified
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="sm:col-span-1">
                      <dt className="text-sm font-medium text-gray-500">Subscription</dt>
                      <dd className="mt-1 text-sm">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          currentUser?.subscriptionType === 'premium' 
                            ? 'bg-purple-100 text-purple-800' 
                            : currentUser?.subscriptionType === 'basic'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                        }`}>
                          {formatSubscriptionType(currentUser?.subscriptionType)}
                        </span>
                      </dd>
                    </div>
                    {(currentUser?.subscriptionType === 'basic' || currentUser?.subscriptionType === 'premium') && currentUser?.subscriptionExpiry && (
                      <div className="sm:col-span-2">
                        <dt className="text-sm font-medium text-gray-500">Subscription Expiry</dt>
                        <dd className="mt-1 text-sm text-gray-900">
                          {new Date(currentUser.subscriptionExpiry).toLocaleDateString()} 
                          <span className="ml-2 text-xs text-gray-500">
                            ({getDaysRemaining(currentUser.subscriptionExpiry)} remaining)
                          </span>
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div className="flex justify-end space-x-3">
                  {isEditing ? (
                    <>
                      <button
                        type="button"
                        onClick={handleCancel}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ml-2"
                      >
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Edit Profile
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile; 